import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { format, formatDistanceToNow } from 'date-fns';
import { CircleArrowUp, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { ActionDropdown } from '@/components/ActionDropdown';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { IPadCommandsSubmenu } from '@/features/ipads/IPadCommandsSubmenu';
import { IPadStatus } from '@/features/ipads/IPadStatus';
import { RenameIPadDialog } from '@/features/ipads/RenameIPadDialog';
import { UnlinkIPadDialog } from '@/features/ipads/UnlinkIPadDialog';

export const Route = createFileRoute('/a/ipads/')({
  staticData: {
    breadcrumbs: [{ label: 'iPads' }],
  },
  component: AdminIPadListPage,
});

type IPad = FunctionReturnType<typeof api.iPads.adminListDevices>[number];

function AdminIPadListPage() {
  const { data: iPads, error, isError, isPending } = useQuery(api.iPads.adminListDevices);
  const { data: appUpdates } = useQuery(api.appUpdates.getAll);
  const [iPadToRename, setIPadToRename] = useState<IPad | null>(null);
  const [iPadToUnlink, setIPadToUnlink] = useState<IPad | null>(null);

  if (isError) return <ErrorAlert message={error.message} title="Error loading iPads" />;
  if (isPending) return <LoadingIcon />;

  return (
    <>
      {iPadToRename?.restaurantId && (
        <RenameIPadDialog
          iPad={iPadToRename}
          onClose={() => setIPadToRename(null)}
          restaurantId={iPadToRename.restaurantId}
        />
      )}
      {iPadToUnlink?.restaurantId && (
        <UnlinkIPadDialog
          iPad={iPadToUnlink}
          onClose={() => setIPadToUnlink(null)}
          restaurantId={iPadToUnlink.restaurantId}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>All iPads</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Restaurant</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Printer</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {iPads.map((iPad) => {
                const { restaurantId, nativeVersion, jsVersion } = iPad;

                // The JS row of the iPad's native version: a minimum or latest JS version only
                // applies to bundles on that native version. An iPad that hasn't reported its
                // versions shows no icons.
                const jsPolicy = appUpdates?.jsPolicies.find(
                  (policy) => policy.app === 'pos-native' && policy.nativeVersion === nativeVersion,
                );
                // Mirror checkAppVersion: the iPad is blocked while its JS version is below its
                // native version's minimum.
                const minJsVersion = jsPolicy?.minJsVersion ?? null;
                const isWaitingForRequiredUpdate =
                  jsVersion != null && minJsVersion !== null && jsVersion < minJsVersion;
                // The device auto-updates to the latest published JS deploy (see pos-native
                // JsUpdates), so an older version is waiting for that update to download/apply.
                const latestJsVersion = jsPolicy?.latestJsVersion ?? null;
                const isWaitingForJsUpdate =
                  jsVersion != null && latestJsVersion !== null && jsVersion < latestJsVersion;

                return (
                  <TableRow key={iPad._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <IPadStatus heartbeats={iPad.heartbeats} />

                        {isWaitingForRequiredUpdate && (
                          <Tooltip>
                            <TooltipTrigger>
                              <TriangleAlert className="h-4 w-4 text-yellow-500" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {`Update required. Waiting for v${minJsVersion} to be applied...`}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{iPad.restaurantName ?? '-'}</TableCell>
                    <TableCell className="font-medium">{iPad.name ?? 'Unnamed iPad'}</TableCell>
                    <TableCell>{iPad.printerName ?? '-'}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>
                          {nativeVersion == null || jsVersion == null
                            ? '-'
                            : `${nativeVersion}.${jsVersion}`}
                        </span>
                        {isWaitingForJsUpdate && (
                          <Tooltip>
                            <TooltipTrigger>
                              <CircleArrowUp className="h-4 w-4 text-blue-500" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {`Waiting to auto-update to v${latestJsVersion}`}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatDistanceToNow(iPad.updatedAt, { addSuffix: true })}
                      </div>
                      <div className="text-muted-foreground">
                        {format(iPad.updatedAt, 'h:mm a d-MMM-yy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionDropdown>
                        {restaurantId && (
                          <IPadCommandsSubmenu
                            restaurantId={restaurantId}
                            posTerminalUuid={iPad.uuid}
                          />
                        )}

                        <DropdownMenuItem asChild>
                          <Link to="/a/ipads/$deviceId" params={{ deviceId: iPad._id }}>
                            View
                          </Link>
                        </DropdownMenuItem>
                        {restaurantId && (
                          <>
                            <DropdownMenuItem onSelect={() => setIPadToRename(iPad)}>
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItemDestructive onSelect={() => setIPadToUnlink(iPad)}>
                              Unlink iPad
                            </DropdownMenuItemDestructive>
                          </>
                        )}
                      </ActionDropdown>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
